<template>
  <header class="header">
    <div class="header-inner container">
      <button class="brand" @click="$router.push('/')">
        <img class="brand-mark" src="/dapp-logo.png" alt="DApp Finance">
        <span class="brand-text">DApp Finance</span>
      </button>

      <div class="header-right">
        <button class="locale-toggle" @click="toggleLocale" :aria-label="locale === 'zh-CN' ? '切换为英文' : 'Switch to Chinese'">
          <span :class="{ active: locale === 'zh-CN' }">中</span><i>/</i><span :class="{ active: locale === 'en-US' }">EN</span>
        </button>
        <template v-if="isLoggedIn">
          <div class="balance-pill">
              <span class="balance-label">{{ $t('余额') }}</span>
            <strong>{{ formatBalance(balance) }}</strong>
            <span>USDT</span>
          </div>

          <button class="user-chip" @click="openDrawer">
            <span class="avatar">
              <img :src="avatarUrl || defaultAvatarUrl" :alt="$t('用户头像')">
            </span>
            <span class="user-meta">
              <strong>{{ shortAddr }}</strong>
              <em>{{ $t('钱包中心') }}</em>
            </span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>

          <transition name="bubble">
            <div class="guide-bubble" v-if="showGuide" @click.stop="showGuide = false">
              <div class="bubble-arrow"></div>
              <div class="bubble-content">
                <span>{{ $t('点击头像管理充值、提现和记录') }}</span>
                <button @click.stop="showGuide = false">{{ $t('知道了') }}</button>
              </div>
            </div>
          </transition>
        </template>

        <button v-else class="connect-btn" @click="connectWallet">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="6" width="20" height="12" rx="2"></rect>
            <path d="M2 10h20"></path>
          </svg>
          <span>{{ $t('连接钱包') }}</span>
        </button>
      </div>
    </div>
  </header>
</template>

<script>
import { formatAmount, shortAddress } from '@/utils/web3'
import { loginWithWallet } from '@/utils/auth'

export default {
  name: 'AppHeader',
  data() {
    return { showGuide: false }
  },
  computed: {
    defaultAvatarUrl() { return '/dapp-logo.png' },
    isLoggedIn() { return this.$store.getters.isLoggedIn },
    locale() { return this.$store.state.locale },
    balance() { return this.$store.getters.balance },
    user() { return this.$store.state.user },
    shortAddr() { return this.user ? shortAddress(this.user.address) : '' },
    avatarUrl() { return this.user && this.user.avatar ? this.assetUrl(this.user.avatar) : '' },
    avatarText() {
      const address = this.user && this.user.address ? this.user.address : ''
      return address ? address.slice(2, 4).toUpperCase() : 'U'
    }
  },
  watch: {
    isLoggedIn(val) {
      if (val) {
        const shown = localStorage.getItem('guide_shown')
        if (!shown) {
          setTimeout(() => { this.showGuide = true }, 800)
          localStorage.setItem('guide_shown', '1')
        }
      }
    }
  },
  methods: {
    toggleLocale() {
      this.$store.commit('SET_LOCALE', this.locale === 'zh-CN' ? 'en-US' : 'zh-CN')
    },
    formatBalance(val) { return formatAmount(val) },
    assetUrl(url) {
      if (!url) return ''
      if (/^https?:\/\//.test(url)) return url
      return url.startsWith('/uploads') ? url : `/uploads/${url.replace(/^\/+/, '')}`
    },
    openDrawer() {
      this.showGuide = false
      this.$store.dispatch('openDrawer')
    },
    async connectWallet() {
      try {
        await loginWithWallet(this.$store)
        this.$toast('登录成功', 'success')
        return
        this.$toast('请在钱包中确认连接...', 'info')
        const address = await getWalletAddress()
        const nonceRes = await getNonce(address)
        if (nonceRes.code !== 0) return this.$toast(nonceRes.message || '获取 nonce 失败', 'error')

        const message = `DApp Finance Login\nNonce: ${nonceRes.data.nonce}`
        this.$toast('请在钱包中签名完成登录...', 'info')
        const signature = await web3SignMessage(message)

        await this.$store.dispatch('login', { address, signature })
        this.$toast('登录成功，欢迎回来', 'success')
      } catch (err) {
        console.error('连接钱包失败:', err)
        if (err && err.message && err.message.includes('MetaMask')) {
          this.$toast('请先安装 MetaMask 钱包插件', 'error')
        } else if (err && err.code === 4001) {
          this.$toast('你取消了签名授权', 'error')
        } else {
          this.$toast('登录失败: ' + (err && err.message ? err.message : '请重试'), 'error')
        }
      }
    }
  }
}
</script>

<style scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(18px) saturate(160%);
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  z-index: 1000;
}
.header-inner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}
.brand {
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}
.brand-mark {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: block;
  object-fit: cover;
}
.brand-text {
  font-size: 18px;
  font-weight: 900;
  color: var(--tech-text);
}
.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}
.locale-toggle {
  height: 34px;
  min-width: 58px;
  padding: 0 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid #d5efda;
  border-radius: 999px;
  background: #effff1;
  color: #7c8d82;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
  transition: .2s ease;
}
.locale-toggle:hover { border-color: #53c878; transform: translateY(-1px); }
.locale-toggle span.active { color: #101512; }
.locale-toggle i { color: #b1c4b6; font-style: normal; }
.balance-pill {
  height: 38px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  border-radius: 999px;
  color: #047857;
  font-size: 12px;
}
.balance-pill strong { font-size: 14px; font-weight: 900; }
.balance-label { color: #059669; }
.user-chip {
  height: 42px;
  display: flex;
  align-items: center;
  gap: 9px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 999px;
  padding: 4px 10px 4px 4px;
  color: #334155;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
}
.user-chip:hover {
  transform: translateY(-1px);
  border-color: #bfdbfe;
  box-shadow: 0 8px 22px rgba(37, 99, 235, 0.12);
}
.avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, #2563eb, #14b8a6);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 900;
  position: relative;
}
.avatar::after {
  content: '';
  position: absolute;
  right: 0;
  bottom: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  border: 1.5px solid #fff;
}
.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.user-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
}
.user-meta strong { font-size: 12px; color: var(--tech-text); }
.user-meta em { font-style: normal; font-size: 10px; color: #94a3b8; margin-top: 3px; }
.connect-btn {
  height: 42px;
  border: none;
  border-radius: 999px;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  font-weight: 850;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.22);
}
.connect-btn:active { transform: translateY(1px); }
.guide-bubble {
  position: absolute;
  top: 54px;
  right: 0;
  z-index: 100;
}
.bubble-content {
  background: var(--tech-black);
  color: #fff;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 12px;
  white-space: nowrap;
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.2);
  display: flex;
  align-items: center;
  gap: 10px;
}
.bubble-content button {
  border: none;
  background: rgba(255,255,255,0.12);
  color: #fff;
  border-radius: 6px;
  padding: 4px 7px;
  cursor: pointer;
  font-size: 11px;
}
.bubble-arrow {
  position: absolute;
  top: -5px;
  right: 24px;
  width: 10px;
  height: 10px;
  background: var(--tech-black);
  transform: rotate(45deg);
  border-radius: 2px;
}
.bubble-enter-active, .bubble-leave-active { transition: opacity 0.2s, transform 0.2s; }
.bubble-enter, .bubble-leave-to { opacity: 0; transform: translateY(6px); }
@media (max-width: 640px) {
  .header { height: 58px; }
  .brand-text { display: none; }
  .balance-pill { display: none; }
  .user-meta { display: none; }
  .user-chip { padding-right: 7px; }
  .connect-btn { height: 38px; padding: 0 14px; font-size: 13px; }
}
</style>
